import apolloClient from '@/lib/apollo-client';
import { CREATE_PROVIDER, UPDATE_PROVIDER } from '@/lib/graphql/mutations/providerMutations';
import { getCookie } from '@/utils/cookies';
import { env } from '@/utils/env';
import { print } from 'graphql';
import { CreateProviderInput, Provider, ProviderFilter, UpdateProviderInput } from '@/features/provider/types';
import { GET_PROVIDER_LOGO, GET_PROVIDER_IMAGE, GET_PROVIDERS, GET_MY_PROVIDERS } from '@/lib/graphql/queries/providerQueries';

export const createProvider = async (input: CreateProviderInput, logo: File | null) => {
  const token = getCookie('token');

  try {
    // Validate required fields before sending to server
    if (!input.name || !input.email || !input.country || !input.city) {
      throw new Error('Missing required fields: name, email, country, and city are required');
    }

    if (!logo) {
      // If no logo, use regular mutation
      const { data } = await apolloClient.mutate({
        mutation: CREATE_PROVIDER,
        variables: { input, logo: null },
        context: {
          headers: {
            Authorization: `Bearer ${token}`
          }
        },
        errorPolicy: 'all' // This will return both data and errors if they exist
      });

      if (!data || !data.createProvider) {
        throw new Error('Failed to create provider - no data returned from server');
      }

      return data.createProvider;
    } else {
      // If logo exists, use multipart form data approach
      const operations = {
        query: print(CREATE_PROVIDER),
        variables: {
          input,
          logo: null
        }
      };

      const formData = new FormData();
      const map = { "0": ["variables.logo"] };
      formData.append("operations", JSON.stringify(operations));
      formData.append("map", JSON.stringify(map));
      formData.append("0", logo);

      const response = await fetch(env.graphqlUrl, {
        method: "POST",
        body: formData,
        headers: {
          "Authorization": `Bearer ${token}`,
          "x-apollo-operation-name": "CreateProvider",
          "apollo-require-preflight": "true"
        }
      });

      const result = await response.json();

      if (result.errors) {
        console.error('GraphQL errors:', JSON.stringify(result.errors, null, 2));
        const errorMessage = result.errors[0]?.message || 'Unknown error occurred';
        throw new Error(`Provider creation failed: ${errorMessage}`);
      }

      if (!result.data || !result.data.createProvider) {
        throw new Error('Failed to create provider - no data returned from server');
      }

      return result.data.createProvider;
    }
  } catch (error) {
    console.error('Error creating provider:', error);
    throw error;
  }
};

export const fetchProviders = async (filter: ProviderFilter) => {
  const token = getCookie('token');

  try {
    // Add debugging to see what's being sent
    console.log('Provider filter being sent to API:', filter);

    // Ensure we have valid filter values before sending to API
    const sanitizedFilter = {
      search: filter.search || null,
      type: (!filter.type || filter.type === '' || filter.type === 'all') ? null : filter.type,
      ownership: (!filter.ownership || filter.ownership === '' || filter.ownership === 'all') ? null : filter.ownership,
      country: filter.country || null,
      city: (!filter.city || filter.city === '' || filter.city === 'all') ? null : filter.city,
      state: (!filter.state || filter.state === '' || filter.state === 'all') ? null : filter.state,
      category: (!filter.category || filter.category === '' || filter.category === 'all') ? null : filter.category,
      page: filter.page || 1,
      limit: filter.limit || 9
    };

    console.log('Sanitized filter being sent to API:', sanitizedFilter);

    const { data } = await apolloClient.query({
      query: GET_PROVIDERS,
      variables: sanitizedFilter,
      context: {
        headers: {
          Authorization: `Bearer ${token}`
        }
      },
      fetchPolicy: 'network-only' // Don't use cache for this query
    });

    if (!data || !data.getProviders) {
      throw new Error('Failed to fetch providers');
    }

    console.log('Provider data received:', data.getProviders);
    return data.getProviders;
  } catch (error) {
    console.error('Error fetching providers:', error);
    throw error;
  }
};

interface ProviderLogo {
  base64Data: string;
  mimetype: string;
}

export const getProviderLogo = async (filename: string): Promise<ProviderLogo | null> => {
  const token = getCookie('token');
  try {
    const { data } = await apolloClient.query({
      query: GET_PROVIDER_LOGO,
      variables: { filename },
      context: {
        headers: {
          Authorization: `Bearer ${token}`
        }
      },
      fetchPolicy: 'no-cache' // Don't cache logo images
    });
    console.log('Provider logo data received:', data);

    // Check if data and getLogoProvider exist before returning
    if (data && data.getLogoProvider) {
      return data.getLogoProvider;
    }

    // Return null if no logo data is found
    return null;
  } catch (error) {
    console.error('Error fetching provider logo:', error);
    return null; // Return null instead of throwing to handle gracefully
  }
};

interface ProviderImage {
  base64Data: string;
  mimetype: string;
}

export const getProviderImage = async (providerId: number, filename: string): Promise<ProviderImage | null> => {
  const token = getCookie('token');
  try {
    const { data } = await apolloClient.query({
      query: GET_PROVIDER_IMAGE,
      variables: { providerId, filename },
      context: {
        headers: {
          Authorization: `Bearer ${token}`
        }
      },
      fetchPolicy: 'no-cache' // Don't cache images
    });
    console.log('Provider image data received:', data);

    // Check if data and getProviderImage exist before returning
    if (data && data.getProviderImage) {
      return data.getProviderImage;
    }

    // Return null if no image data is found
    return null;
  } catch (error) {
    console.error('Error fetching provider image:', error);
    return null; // Return null instead of throwing to handle gracefully
  }
};

export const fetchMyProviders = async (filter: ProviderFilter) => {
  const token = getCookie('token');

  try {
    // Add debugging to see what's being sent
    console.log('My providers filter being sent to API:', filter);

    // Ensure we have valid filter values before sending to API
    const sanitizedFilter = {
      search: filter.search || null,
      type: (!filter.type || filter.type === '' || filter.type === 'all') ? null : filter.type,
      ownership: (!filter.ownership || filter.ownership === '' || filter.ownership === 'all') ? null : filter.ownership,
      country: filter.country || null,
      city: (!filter.city || filter.city === '' || filter.city === 'all') ? null : filter.city,
      state: (!filter.state || filter.state === '' || filter.state === 'all') ? null : filter.state,
      category: (!filter.category || filter.category === '' || filter.category === 'all') ? null : filter.category,
      page: filter.page || 1,
      limit: filter.limit || 10
    };

    console.log('Sanitized filter being sent to API for my providers:', sanitizedFilter);

    const { data } = await apolloClient.query({
      query: GET_MY_PROVIDERS,
      variables: sanitizedFilter,
      context: {
        headers: {
          Authorization: `Bearer ${token}`
        }
      },
      fetchPolicy: 'network-only' // Don't use cache for this query
    });

    if (!data || !data.getMyProviders) {
      throw new Error('Failed to fetch my providers');
    }

    console.log('My providers data received:', data.getMyProviders);
    return data.getMyProviders;
  } catch (error) {
    console.error('Error fetching my providers:', error);
    throw error;
  }
};

export const updateProviderService = async (input: UpdateProviderInput, logo: File | null, images: File[] = []) => {
  const token = getCookie('token');

  try {
    // Ensure input.id is a number and not undefined
    if (input.id === undefined || input.id === null) {
      throw new Error('Provider ID is required for update operation');
    }

    // Convert to number if it's a string
    const providerId = typeof input.id === 'string' ? parseInt(input.id, 10) : input.id;

    if (isNaN(providerId)) {
      throw new Error('Invalid provider ID format');
    }

    // Create a clean input object for the backend
    const adaptedInput = {
      id: providerId,
      name: input.name || undefined,
      city: input.city || undefined,
      country: input.country,
      state: input.state || undefined,
      postalCode: input.postalCode || undefined,
      address: input.address || undefined,
      description: input.description || undefined,
      yearFounded: input.yearFounded || undefined,
      type: input.type || undefined,
      website: input.website || undefined,
      email: input.email || undefined,
      phone: input.phone || undefined,
      linkedin: input.linkedin || undefined,
      x: input.x || undefined,
      ownership: input.ownership || undefined,
      category: input.category || undefined,
      languages: input.languages || undefined,
      degreeTypes: input.degreeTypes || undefined,
      removeImages: input.removeImages || undefined,
      existingImages: input.existingImages || undefined // Include existing images
    };

    // If there are no files to upload, use the regular mutation
    if (!logo && images.length === 0) {
      const { data } = await apolloClient.mutate({
        mutation: UPDATE_PROVIDER,
        variables: {
          id: providerId,
          input: adaptedInput
        },
        context: {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      });

      return data.updateProvider;
    }

    // For file uploads, use multipart request
    const formData = new FormData();

    // Add the JSON input as a blob
    formData.append('operations', JSON.stringify({
      query: print(UPDATE_PROVIDER),
      variables: {
        id: providerId,
        input: adaptedInput,
        logo: null,
        images: Array(images.length).fill(null)
      }
    }));

    // Create the map object for files
    const map: Record<string, string[]> = {};
    let fileIndex = 0;

    // Add logo if provided
    if (logo) {
      map[fileIndex] = [`variables.logo`];
      fileIndex++;
    }

    // Add additional images if provided
    if (images.length > 0) {
      images.forEach((_, index) => {
        map[fileIndex + index] = [`variables.images.${index}`];
      });
    }

    // Add the map as a string
    formData.append('map', JSON.stringify(map));

    // Add the actual files
    if (logo) {
      formData.append('0', logo);
    }

    if (images.length > 0) {
      images.forEach((image, index) => {
        const fileKey = logo ? index + 1 : index;
        formData.append(`${fileKey}`, image);
      });
    }

    // Make the request
    const response = await fetch(env.graphqlUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'apollo-require-preflight': 'true'
      },
      body: formData
    });

    const result = await response.json();

    if (result.errors) {
      throw new Error(result.errors[0].message);
    }

    return result.data.updateProvider;
  } catch (error) {
    console.error('Error updating provider:', error);
    throw error;
  }
};





